'use client';

// Two-card vertical entry (FR-11 / D12): "Compare your helpdesk" and "Compare
// your AI layer". Clicking a card reveals that vertical's two dropdowns in place
// (no navigation until both products are chosen). This is a progressive-
// enhancement filter ONLY — the page also server-renders a crawlable link list,
// so crawlers never depend on this interaction (IDX-1/IDX-4).

import { useState } from 'react';
import { Headset, Sparkles, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ProductSelector, type ProductOption } from './product-selector';

type Vertical = 'helpdesk' | 'ai-agent';

export function VerticalChooser({ helpdeskOptions }: { helpdeskOptions: ProductOption[] }) {
  const [active, setActive] = useState<Vertical | null>('helpdesk');

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Card
          icon={<Headset className="h-5 w-5" />}
          title="Compare your helpdesk"
          subtitle="Zendesk, Intercom, Freshdesk, Gorgias, and 13 others, side by side."
          selected={active === 'helpdesk'}
          onClick={() => setActive('helpdesk')}
        />
        <Card
          icon={<Sparkles className="h-5 w-5" />}
          title="Compare your AI layer"
          subtitle="AI agents that run on top of your helpdesk."
          badge="Coming soon"
          disabled
          selected={active === 'ai-agent'}
          onClick={() => {}}
        />
      </div>

      {active === 'helpdesk' && (
        <div className="mt-6 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
          <p className="mb-3 text-sm font-medium text-neutral-700">
            Pick two, then hit compare
          </p>
          <ProductSelector vertical="helpdesk" options={helpdeskOptions} />
        </div>
      )}
    </div>
  );
}

function Card({
  icon,
  title,
  subtitle,
  badge,
  disabled,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge?: string;
  disabled?: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'group flex items-start gap-4 rounded-xl border p-5 text-left transition-colors',
        disabled
          ? 'cursor-not-allowed border-neutral-200 bg-neutral-50 opacity-70'
          : selected
            ? 'border-neutral-900 bg-white shadow-sm'
            : 'border-neutral-200 bg-white hover:border-neutral-400',
      )}
    >
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
          selected ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="font-medium text-neutral-900">{title}</span>
          {badge && (
            <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              {badge}
            </span>
          )}
          {!disabled && (
            <ChevronRight className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5" />
          )}
        </span>
        <span className="mt-1 block text-sm text-neutral-500">{subtitle}</span>
      </span>
    </button>
  );
}
